import React, { useState, useEffect, memo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  InputAdornment,
  Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from '../../utils/axiosConfig';

// TalentCalls Component
const TalentCalls = ({ data, onUpdate, projectId, styles, onUpdateBackground }) => {
  const [loading, setLoading] = useState(false);
  const [projectCast, setProjectCast] = useState([]);
  const [error, setError] = useState(null);
  const [castSectionExpanded, setCastSectionExpanded] = useState(false);
  const theme = useTheme();
  // UI controls
  const [denseMode, setDenseMode] = useState(false);
  // Toolbar filters
  const [talentSearch, setTalentSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // Background entries local state
  const [backgroundEntries, setBackgroundEntries] = useState(Array.isArray(data.background) ? data.background : []);
  
  // Backfill CAST # from Cast.js (hierarchy/rank) if missing
  useEffect(() => {
    const calls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    if (!projectCast || projectCast.length === 0 || calls.length === 0) return;
    let changed = false;
    const next = calls.map(t => {
      if (t.castNumber && String(t.castNumber).trim() !== '') return t;
      const match = projectCast.find(c => 
        (c.name && t.name && c.name.trim().toLowerCase() === t.name.trim().toLowerCase()) ||
        (c.characterName && t.character && c.characterName.trim().toLowerCase() === t.character.trim().toLowerCase())
      );
      if (match) {
        const num = (typeof match.rank === 'number' && match.rank > 0) ? match.rank : match.hierarchy;
        if (num) {
          changed = true;
          return { ...t, castNumber: num };
        }
      }
      return t;
    });
    if (changed) {
      onUpdate(next);
    }
  }, [projectCast, data.talentCalls, onUpdate]);
  
  // Function to get color based on hierarchy number
  const getHierarchyColor = (hierarchy) => {
    switch(Number(hierarchy)) {
      case 1: return '#FFD700'; // Lead - Gold
      case 2: return '#C0C0C0'; // Supporting - Silver
      case 3: return '#CD7F32'; // Featured - Bronze
      case 4: return '#4682B4'; // Minor Supporting - Steel Blue
      case 5: return '#708090'; // Minor - Slate Gray
      case 6: return '#A9A9A9'; // Extra - Dark Gray
      case 7: return '#D3D3D3'; // Background - Light Gray
      default: return '#708090'; // Default - Slate Gray
    }
  };

  // Default auto-populate when schedule or project cast changes
  useEffect(() => {
    autoPopulateFromSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.schedule, projectCast]);

  useEffect(() => {
    // Only fetch if we have a projectId
    if (projectId) {
      fetchProjectCast();
    }
  }, [projectId]);

  // Keep backgroundEntries in sync if parent data changes
  useEffect(() => {
    setBackgroundEntries(Array.isArray(data.background) ? data.background : []);
  }, [data.background]);

  // Autosave background entries on change (debounced)
  useEffect(() => {
    if (typeof onUpdateBackground !== 'function') return;
    const timer = setTimeout(() => {
      onUpdateBackground(backgroundEntries);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundEntries]);

  const fetchProjectCast = async () => {
    setLoading(true);
    setError(null);
    try {
      // Keep the same API endpoint but add x-auth-token header
      const response = await axios.get(`/api/v1/cast/project/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')
        }
      });
      setProjectCast(response.data);
    } catch (err) {
      console.error('Error fetching cast:', err);
      setError('Failed to load cast data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTalentCall = () => {
    const updatedCalls = [...(data.talentCalls || []), { 
      name: '', 
      character: '', 
      castNumber: '',
      callTime: '', 
      setCallTime: '',
      pickupTime: '', 
      status: 'SW',
      notes: '' 
    }];
    onUpdate(updatedCalls);
  };

  const handleAddCastMemberToCall = (castMember) => {
    console.log('Adding cast member to callsheet:', castMember);
    console.log('Current data.talentCalls:', data.talentCalls);
    
    // Format the cast member data for the call sheet
    const newTalentCall = {
      name: castMember.name,
      character: castMember.characterName,
      castNumber: castMember.hierarchy || castMember.castNumber || '',
      callTime: '',
      setCallTime: '',
      pickupTime: '',
      status: 'SW',
      notes: ''
    };
    console.log('New talent call object:', newTalentCall);
    
    // Add to the talent calls - ensure talentCalls is an array before spreading
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    console.log('Existing calls after check:', existingCalls);
    
    const updatedCalls = [...existingCalls, newTalentCall];
    console.log('Updated calls array:', updatedCalls);
    
    // Call onUpdate with just the array
    console.log('Calling onUpdate with talent calls array:', updatedCalls);
    onUpdate(updatedCalls);
    console.log('onUpdate called');
  };

  const handleUpdateTalentCall = (index, field, value) => {
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    const updatedCalls = [...existingCalls];
    updatedCalls[index] = { ...updatedCalls[index], [field]: value };
    onUpdate(updatedCalls);
  };

  const handleDeleteTalentCall = (index) => {
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    const updatedCalls = [...existingCalls];
    updatedCalls.splice(index, 1);
    onUpdate(updatedCalls);
  };

  const toggleCastSection = () => {
    setCastSectionExpanded(!castSectionExpanded);
  };

  // Derive displayed calls with filters
  const displayedCalls = (Array.isArray(data.talentCalls) ? [...data.talentCalls] : [])
    .filter(t => {
      if (!talentSearch) return true;
      const s = talentSearch.toLowerCase();
      return (
        (t.name && t.name.toLowerCase().includes(s)) ||
        (t.character && t.character.toLowerCase().includes(s)) ||
        (t.notes && t.notes.toLowerCase().includes(s))
      );
    })
    .filter(t => statusFilter === 'ALL' ? true : (String(t.status || '').toUpperCase() === statusFilter));

  // Padding constants depend on density mode
  const cellPadY = denseMode ? 0.5 : 1;
  const cellPadX = denseMode ? 1 : 1.25;
  const headPadY = denseMode ? 0.75 : 1.25;
  const headPadX = denseMode ? 1 : 1.25;

  // Suggested Background Description options
  const backgroundDescriptionOptions = [
    'Stand-In',
    'Photo Double',
    'Pedestrians',
    'Party Guests',
    'Restaurant Patrons',
    'Office Workers',
    'Students',
    'Police Officers',
    'Nurses',
    'Doctors',
    'Security Guards',
    'Bar Patrons',
    'Spectators',
    'Crowd',
    'Audience',
    'Shoppers',
    'Drivers',
    'Team Fans'
  ];

  // Auto-populate talent calls from today's schedule (data.schedule)
  const autoPopulateFromSchedule = () => {
    try {
      const schedule = Array.isArray(data?.schedule) ? data.schedule : [];
      if (schedule.length === 0 || projectCast.length === 0) return;

      // Collect character names from schedule cast strings (comma-separated), case-insensitive
      const characterSet = new Set(); // stores normalized lowercase names
      const originalNameByNorm = new Map(); // map normalized -> first-seen original for display
      schedule.forEach(item => {
        const castStr = item.cast || '';
        const parts = Array.isArray(castStr) ? castStr : String(castStr).split(',');
        parts.forEach(p => {
          const original = String(p).trim();
          if (!original) return;
          const norm = original.toLowerCase();
          if (!characterSet.has(norm)) {
            characterSet.add(norm);
            originalNameByNorm.set(norm, original);
          }
        });
      });

      if (characterSet.size === 0) return;

      const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];

      // Helper: does a call already exist for this cast member or character
      const hasCall = (castMember, normChar, pending = []) => {
        const existsIn = (arr) => arr.some(call => {
          const sameName = call.name && castMember.name && call.name.trim().toLowerCase() === castMember.name.trim().toLowerCase();
          const sameChar = call.character && castMember.characterName && call.character.trim().toLowerCase() === castMember.characterName.trim().toLowerCase();
          const sameNormChar = call.character && call.character.trim().toLowerCase() === normChar;
          return sameName || sameChar || sameNormChar;
        });
        return existsIn(existingCalls) || existsIn(pending);
      };

      // Build new calls for matched cast with valid hierarchy
      const newCalls = [];
      const addedCastIds = new Set();
      characterSet.forEach(normChar => {
        const match = projectCast.find(cm => cm.characterName && cm.characterName.trim().toLowerCase() === normChar);
        if (!match) return; // only if associated to Cast.js
        const number = (typeof match.hierarchy === 'number' && match.hierarchy > 0)
          ? match.hierarchy
          : (typeof match.rank === 'number' && match.rank > 0 ? match.rank : null);
        if (!number) return; // require a number associated

        const castId = match._id || `${(match.name||'').toLowerCase()}::${(match.characterName||'').toLowerCase()}`;
        if (addedCastIds.has(castId)) return; // avoid duplicates in same run
        if (hasCall(match, normChar, newCalls)) return; // skip if already present or will be added

        addedCastIds.add(castId);
        newCalls.push({
          name: match.name || '',
          character: match.characterName || originalNameByNorm.get(normChar) || '',
          castNumber: number,
          callTime: '',
          setCallTime: '',
          pickupTime: '',
          status: 'SW',
          notes: ''
        });
      });

      if (newCalls.length > 0) {
        onUpdate([...(existingCalls || []), ...newCalls]);
      }
    } catch (e) {
      console.warn('Auto-populate from schedule failed:', e);
    }
  };

  // Background handlers
  const handleBgChange = (index, field, value) => {
    setBackgroundEntries(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddBg = () => {
    setBackgroundEntries(prev => ([...prev, { qty: '', call: '', description: '' }]));
  };

  const handleDeleteBg = (index) => {
    setBackgroundEntries(prev => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleSaveBackground = () => {
    if (typeof onUpdateBackground === 'function') {
      onUpdateBackground(backgroundEntries);
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 4, 
        mb: 4,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper,
        ...(styles?.primarySection || {})
      }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.5px',
                fontSize: '1.75rem',
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Talent Calls
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.875rem'
              }}
            >
              Manage cast call times and assignments
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`${data.talentCalls?.length || 0} Talent`} 
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            '& .MuiChip-label': {
              px: 2
            }
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress color="error" />
        </Box>
      ) : error ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 3, 
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.4)' : '#f9f9f9', 
          borderRadius: 1 
        }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <>
          {/* Project Cast Selection - Collapsible */}
          {projectId && projectCast.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Button 
                onClick={toggleCastSection}
                variant="outlined"
                fullWidth
                sx={{ 
                  justifyContent: 'space-between', 
                  mb: 2,
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 1,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  borderWidth: 1.5,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                endIcon={castSectionExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              >
                ADD FROM PROJECT CAST ({projectCast.length})
              </Button>
              
              {castSectionExpanded && (
                <Box sx={{ 
                  p: 2, 
                  border: `1px dashed ${theme.palette.mode === 'dark' ? theme.palette.error.light : '#f44336'}`, 
                  borderRadius: 1, 
                  mt: 1, 
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.4)' : 'inherit' 
                }}>
                  <Grid container spacing={2.5}>
                    {projectCast.map((castMember) => (
                      <Grid item xs={12} sm={6} md={4} key={castMember._id}>
                        <Paper 
                          elevation={0} 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.2)' : '#ffebee',
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.error.light : '#f44336'
                            },
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.6)' : 'inherit',
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'inherit'
                          }}
                          onClick={() => handleAddCastMemberToCall(castMember)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Hierarchy badge */}
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: getHierarchyColor(castMember.hierarchy),
                                color: '#000',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                border: '1px solid rgba(0,0,0,0.2)',
                                flexShrink: 0
                              }}
                            >
                              {castMember.hierarchy || '?'}
                            </Box>
                            
                            <Box>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit'
                                }}
                              >
                                {castMember.name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                as <strong>{castMember.characterName}</strong>
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {castMember.role}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Toolbar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Manage your talent call times and notes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Add talent call" arrow>
              <span>
                <IconButton
                  onClick={handleAddTalentCall}
                  aria-label="Add talent call"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    color: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.9)
                      : theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
              <TextField
                size="small"
                placeholder="Search by name or character..."
                value={talentSearch}
                onChange={(e) => setTalentSearch(e.target.value)}
                sx={{ width: 260 }}
              />
              <Select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ width: 150 }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="SW">SW</MenuItem>
                <MenuItem value="W">W</MenuItem>
                <MenuItem value="H">H</MenuItem>
                <MenuItem value="R">R</MenuItem>
                <MenuItem value="F">F</MenuItem>
                <MenuItem value="ND">ND</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Talent Calls Table */}
          <TableContainer 
            component={Paper} 
            variant="outlined" 
            sx={{ 
              mt: 2, 
              mb: 3, 
              maxHeight: '60vh', 
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 4px 16px rgba(0,0,0,0.08)',
              backgroundColor: theme.palette.mode === 'dark'
                ? '#1e2124'
                : theme.palette.background.paper,
              ...styles?.table 
            }}
          >
            <Table 
              size="small" 
              stickyHeader 
              sx={{ 
                '& .MuiTableCell-root': { 
                  py: cellPadY, 
                  px: cellPadX,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? '#262a2e'
                    : alpha(theme.palette.background.paper, 0.98),
                  borderBottom: `2px solid ${alpha(theme.palette.error.main, 0.1)}`,
                  py: headPadY,
                  px: headPadX
                }
              }}
            >
              <TableHead>
                <TableRow sx={styles?.talentTableHeader || { backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.dark, 0.2) : '#ffebee' }}>
                  <TableCell sx={{ width: 60, fontWeight: 700, textAlign: 'center', letterSpacing: '0.5px' }}>#</TableCell>
                  <TableCell sx={{ width: 220, fontWeight: 700, letterSpacing: '0.5px' }}>CHARACTER</TableCell>
                  <TableCell sx={{ width: 220, fontWeight: 700, letterSpacing: '0.5px' }}>NAME</TableCell>
                  <TableCell sx={{ width: 90, fontWeight: 700, textAlign: 'center', letterSpacing: '0.5px' }}>STATUS</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 'bold', textAlign: 'center' }}>CALL</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 'bold', textAlign: 'center' }}>PICKUP</TableCell>
                  <TableCell sx={{ width: 140, fontWeight: 'bold', textAlign: 'center' }}>SET</TableCell>
                  <TableCell sx={{ minWidth: 280, fontWeight: 'bold' }}>NOTES</TableCell>
                  <TableCell align="center" sx={{ width: 72, fontWeight: 'bold' }}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCalls.map((talent, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:nth-of-type(odd)': { 
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.4)' : '#f9f9f9' 
                      }
                    }}
                  >
                    <TableCell sx={{ textAlign: 'center' }}>
                      <TextField
                        placeholder="#"
                        value={talent.castNumber || ''}
                        onChange={(e) => handleUpdateTalentCall(index, 'castNumber', e.target.value)}
                        size="small"
                        sx={{
                          width: '50px',
                          '& .MuiInputBase-input': {
                            px: 1
                          },
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                        inputProps={{ style: { textAlign: 'center' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        placeholder="Character"
                        value={talent.character || ''}
                        onChange={(e) => handleUpdateTalentCall(index, 'character', e.target.value)}
                        size="small"
                        sx={{
                          width: '100%',
                          '& .MuiInputBase-input': { px: 1.25 },
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          placeholder="Name"
                          value={talent.name || ''}
                          onChange={(e) => handleUpdateTalentCall(index, 'name', e.target.value)}
                          size="small"
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.default, 0.3)
                                : alpha(theme.palette.grey[50], 0.5),
                              borderRadius: 1,
                              '& fieldset': {
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.05)
                                  : alpha(theme.palette.primary.main, 0.02),
                                '& fieldset': {
                                  borderWidth: 1.5
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Select
                        size="small"
                        value={talent.status || 'SW'}
                        onChange={(e) => handleUpdateTalentCall(index, 'status', e.target.value)}
                        sx={{
                          width: 80,
                          borderRadius: 1,
                          '& .MuiSelect-select': {
                            textAlign: 'center',
                            py: 0.5,
                            px: 1,
                            fontWeight: 600
                          },
                          '& .MuiInputBase-root': {
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? alpha(theme.palette.background.paper, 0.8) 
                              : theme.palette.background.paper,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            '&:hover': {
                              borderColor: alpha(theme.palette.error.main, 0.2)
                            },
                            '&.Mui-focused': {
                              borderColor: theme.palette.error.main,
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, 0.1)}`
                            }
                          }
                        }}
                      >
                        <MenuItem value="SW">SW</MenuItem>
                        <MenuItem value="W">W</MenuItem>
                        <MenuItem value="H">H</MenuItem>
                        <MenuItem value="R">R</MenuItem>
                        <MenuItem value="F">F</MenuItem>
                        <MenuItem value="ND">ND</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                        <TimePicker
                          ampm
                          inputFormat="hh:mm a"
                          value={talent.callTime ? new Date(`2000-01-01T${talent.callTime}`) : null}
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleUpdateTalentCall(index, 'callTime', '');
                              return;
                            }
                            try {
                              let h, m;
                              if (typeof newValue?.getHours === 'function') {
                                h = newValue.getHours();
                                m = newValue.getMinutes();
                              } else if (typeof newValue?.hour === 'function') {
                                h = newValue.hour();
                                m = newValue.minute();
                              } else if (newValue?.$d instanceof Date) {
                                h = newValue.$d.getHours();
                                m = newValue.$d.getMinutes();
                              } else if (typeof newValue?.toDate === 'function') {
                                const d = newValue.toDate();
                                h = d.getHours();
                                m = d.getMinutes();
                              }
                              if (typeof h === 'number' && typeof m === 'number') {
                                const hh = String(h).padStart(2, '0');
                                const mm = String(m).padStart(2, '0');
                                handleUpdateTalentCall(index, 'callTime', `${hh}:${mm}`);
                              }
                            } catch {}
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Call"
                              size="small"
                              sx={{
                                width: 140,
                                '& .MuiInputBase-input': { textAlign: 'center' },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.05)
                                      : alpha(theme.palette.primary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                    </TableCell>
                    <TableCell>
                        <TimePicker
                          ampm
                          inputFormat="hh:mm a"
                          value={talent.pickupTime ? new Date(`2000-01-01T${talent.pickupTime}`) : null}
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleUpdateTalentCall(index, 'pickupTime', '');
                              return;
                            }
                            try {
                              let h, m;
                              if (typeof newValue?.getHours === 'function') {
                                h = newValue.getHours();
                                m = newValue.getMinutes();
                              } else if (typeof newValue?.hour === 'function') {
                                h = newValue.hour();
                                m = newValue.minute();
                              } else if (newValue?.$d instanceof Date) {
                                h = newValue.$d.getHours();
                                m = newValue.$d.getMinutes();
                              } else if (typeof newValue?.toDate === 'function') {
                                const d = newValue.toDate();
                                h = d.getHours();
                                m = d.getMinutes();
                              }
                              if (typeof h === 'number' && typeof m === 'number') {
                                const hh = String(h).padStart(2, '0');
                                const mm = String(m).padStart(2, '0');
                                handleUpdateTalentCall(index, 'pickupTime', `${hh}:${mm}`);
                              }
                            } catch {}
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Pickup"
                              size="small"
                              sx={{
                                width: 140,
                                '& .MuiInputBase-input': { textAlign: 'center' },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.05)
                                      : alpha(theme.palette.primary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                    </TableCell>
                    <TableCell>
                        <TimePicker
                          ampm
                          inputFormat="hh:mm a"
                          value={talent.setCallTime ? new Date(`2000-01-01T${talent.setCallTime}`) : null}
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleUpdateTalentCall(index, 'setCallTime', '');
                              return;
                            }
                            try {
                              let h, m;
                              if (typeof newValue?.getHours === 'function') {
                                h = newValue.getHours();
                                m = newValue.getMinutes();
                              } else if (typeof newValue?.hour === 'function') {
                                h = newValue.hour();
                                m = newValue.minute();
                              } else if (newValue?.$d instanceof Date) {
                                h = newValue.$d.getHours();
                                m = newValue.$d.getMinutes();
                              } else if (typeof newValue?.toDate === 'function') {
                                const d = newValue.toDate();
                                h = d.getHours();
                                m = d.getMinutes();
                              }
                              if (typeof h === 'number' && typeof m === 'number') {
                                const hh = String(h).padStart(2, '0');
                                const mm = String(m).padStart(2, '0');
                                handleUpdateTalentCall(index, 'setCallTime', `${hh}:${mm}`);
                              }
                            } catch {}
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Set"
                              size="small"
                              sx={{
                                width: 140,
                                '& .MuiInputBase-input': { textAlign: 'center' },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.05)
                                      : alpha(theme.palette.primary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                    </TableCell>
                    <TableCell sx={{ minWidth: 280 }}>
                      <TextField
                        fullWidth
                        placeholder="Notes"
                        value={talent.notes || ''}
                        onChange={(e) => handleUpdateTalentCall(index, 'notes', e.target.value)}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteTalentCall(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Background / Stand-Ins Section */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.secondary.main, 0.15)
                      : alpha(theme.palette.secondary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', color: theme.palette.text.primary }}>
                    Background / Stand-Ins
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                    Extras and photo doubles
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Add background entry" arrow>
                <span>
                  <IconButton
                    onClick={handleAddBg}
                    aria-label="Add background entry"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      color: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.9)
                        : theme.palette.secondary.main,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.12)
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0,0,0,0.4)'
                  : '0 4px 16px rgba(0,0,0,0.08)',
                backgroundColor: theme.palette.mode === 'dark'
                  ? '#1e2124'
                  : theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}
            >
              <Table size={denseMode ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80, fontWeight: 700 }}>QTY</TableCell>
                    <TableCell sx={{ width: 140, fontWeight: 700, textAlign: 'center' }}>CALL</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>DESCRIPTION / NOTES</TableCell>
                    <TableCell sx={{ width: 72, textAlign: 'center', fontWeight: 700 }}>ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(backgroundEntries) ? backgroundEntries : []).map((bg, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          placeholder="Qty"
                          size="small"
                          value={bg.qty || ''}
                          onChange={(e) => handleBgChange(idx, 'qty', e.target.value)}
                          sx={{ 
                            width: 80,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.default, 0.3)
                                : alpha(theme.palette.grey[50], 0.5),
                              borderRadius: 1,
                              '& fieldset': {
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.secondary.main
                              },
                              '&.Mui-focused': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.secondary.main, 0.05)
                                  : alpha(theme.palette.secondary.main, 0.02),
                                '& fieldset': {
                                  borderWidth: 1.5
                                }
                              }
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TimePicker
                          ampm
                          inputFormat="hh:mm a"
                          value={bg.call ? new Date(`2000-01-01T${bg.call}`) : null}
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleBgChange(idx, 'call', '');
                              return;
                            }
                            try {
                              let h, m;
                              if (typeof newValue?.getHours === 'function') {
                                h = newValue.getHours();
                                m = newValue.getMinutes();
                              } else if (typeof newValue?.hour === 'function') {
                                h = newValue.hour();
                                m = newValue.minute();
                              } else if (newValue?.$d instanceof Date) {
                                h = newValue.$d.getHours();
                                m = newValue.$d.getMinutes();
                              } else if (typeof newValue?.toDate === 'function') {
                                const d = newValue.toDate();
                                h = d.getHours();
                                m = d.getMinutes();
                              }
                              if (typeof h === 'number' && typeof m === 'number') {
                                const hh = String(h).padStart(2, '0');
                                const mm = String(m).padStart(2, '0');
                                handleBgChange(idx, 'call', `${hh}:${mm}`);
                              }
                            } catch {}
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Call"
                              size="small"
                              sx={{ 
                                width: 140, 
                                '& .MuiInputBase-input': { textAlign: 'center' },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.secondary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : alpha(theme.palette.secondary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          freeSolo
                          options={backgroundDescriptionOptions}
                          value={bg.description || ''}
                          onChange={(event, newValue) => {
                            handleBgChange(idx, 'description', newValue || '');
                          }}
                          onInputChange={(event, newInputValue) => {
                            // Keep state in sync while typing
                            handleBgChange(idx, 'description', newInputValue || '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Description / Notes"
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.secondary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : alpha(theme.palette.secondary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" size="small" onClick={() => handleDeleteBg(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!backgroundEntries || backgroundEntries.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                        No background added.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          
        </>
      )}
    </Paper>
  );
};

export default memo(TalentCalls);
